import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import PageHeader from "../components/layout/PageHeader";
import { Calendar, TrendingUp, Award, Clock, BarChart3, CheckCircle2, XCircle } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const DhikrHistory = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const { user } = useAuth();

  const [history, setHistory] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(() => getLocalDate());

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return isEnglish
      ? date.toLocaleDateString('en-US', options)
      : date.toLocaleDateString('id-ID', options);
  };

  // Load history for selected date
  useEffect(() => {
    loadHistory(selectedDate);
  }, [selectedDate]);

  // Load weekly stats
  useEffect(() => {
    loadWeeklyStats();
  }, []);

  const loadHistory = async (date) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/dzikir/logs/${date}`);
      setHistory(res.data || []);
    } catch (error) {
      console.error("Error loading history:", error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      // Get stats for last 7 days
      const stats = [];
      const today = new Date();

      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateString = getLocalDate(date);

        try {
          const res = await apiClient.get(`/dzikir/logs/${dateString}`);
          const total = (res.data || []).reduce((sum, log) => {
            const logType = log.log_type || "count";
            if (logType === "timer") return sum;
            return sum + (log.count || 0);
          }, 0);
          stats.push({
            date: dateString,
            total: total,
            logs: res.data || []
          });
        } catch (error) {
          stats.push({
            date: dateString,
            total: 0,
            logs: []
          });
        }
      }

      setWeeklyStats(stats);
    } catch (error) {
      console.error("Error loading weekly stats:", error);
    }
  };

  const getDhikirLabel = (dzikirId, logType) => {
    const labels = {
      'tasbih-subhanallah': {
        en: 'SubhanAllah',
        id: 'SubhanAllah',
        arabic: 'سُبْحَانَ اللَّهِ',
        icon: '🌟',
        target: 33
      },
      'tasbih-alhamdulillah': {
        en: 'Alhamdulillah',
        id: 'Alhamdulillah',
        arabic: 'الْحَمْدُ لِلَّهِ',
        icon: '🙏',
        target: 33
      },
      'tasbih-allahuakbar': {
        en: 'Allahu Akbar',
        id: 'Allahu Akbar',
        arabic: 'اللَّهُ أَكْبَرُ',
        icon: '🌙',
        target: 34
      }
    };

    if (logType === "timer" || dzikirId === "timer-session") {
      return {
        en: "Dhikr Timer",
        id: "Timer Dzikir",
        arabic: "",
        icon: "⏱️",
        target: 0,
        isTimer: true
      };
    }

    return labels[dzikirId] || {
      en: dzikirId,
      id: dzikirId,
      arabic: '',
      icon: '📿',
      target: 0,
      isTimer: false
    };
  };

  const calculateDailyTotal = (logs) => {
    return logs.reduce((sum, log) => {
      const logType = log.log_type || "count";
      if (logType === "timer") return sum;
      return sum + (log.count || 0);
    }, 0);
  };

  const calculateCompletionPercentage = (total) => {
    return Math.min((total / 100) * 100, 100);
  };

  const formatDuration = (durationSeconds) => {
    if (!durationSeconds) return "0m";
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const getStreak = () => {
    let streak = 0;
    for (let i = weeklyStats.length - 1; i >= 0; i--) {
      if (weeklyStats[i].total > 0) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(getLocalDate(date));
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={isEnglish ? "Dhikr History" : "Riwayat Dzikir"}
        showBack
        onBackClick={() => navigate("/doa")}
      />

      <div className="page-container space-y-4 sm:space-y-5">
        {/* Date Selector */}
        <Card>
          <CardContent className="p-2.5 sm:p-3 md:p-4">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                onClick={() => changeDate(-1)}
              >
                ←
              </Button>
              <div className="text-center flex-1 mx-2 sm:mx-4 min-w-0">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 text-primary" />
                <p className="font-semibold text-xs sm:text-sm truncate">
                  {formatDate(selectedDate)}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {selectedDate === getLocalDate()
                    ? isEnglish ? "Today" : "Hari Ini"
                    : ""}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                onClick={() => changeDate(1)}
                disabled={selectedDate >= getLocalDate()}
              >
                →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Daily Summary */}
        {loading ? (
          <Card>
            <CardContent className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
              <Skeleton className="h-16 sm:h-20 w-full" />
              <Skeleton className="h-24 sm:h-32 w-full" />
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="islamic-gradient text-white">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                  <div>
                    <Award className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2" />
                    <p className="text-xl sm:text-2xl font-bold">{calculateDailyTotal(history)}</p>
                    <p className="text-[10px] sm:text-xs text-white/80">
                      {isEnglish ? "Total Today" : "Total Hari Ini"}
                    </p>
                  </div>
                  <div>
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2" />
                    <p className="text-xl sm:text-2xl font-bold">{getStreak()}</p>
                    <p className="text-[10px] sm:text-xs text-white/80">
                      {isEnglish ? "Day Streak" : "Streak Hari"}
                    </p>
                  </div>
                  <div>
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1.5 sm:mb-2" />
                    <p className="text-xl sm:text-2xl font-bold">
                      {calculateCompletionPercentage(calculateDailyTotal(history)).toFixed(0)}%
                    </p>
                    <p className="text-[10px] sm:text-xs text-white/80">
                      {isEnglish ? "Completion" : "Penyelesaian"}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-3 sm:mt-4">
                  <div className="bg-white/20 rounded-full h-2 sm:h-3 overflow-hidden">
                    <div
                      className="bg-white h-full transition-all duration-500"
                      style={{
                        width: `${calculateCompletionPercentage(calculateDailyTotal(history))}%`
                      }}
                    />
                  </div>
                  <p className="text-[10px] sm:text-xs text-center mt-1.5 sm:mt-2 text-white/80">
                    {calculateDailyTotal(history)}/100 {isEnglish ? "dhikr completed" : "dzikir selesai"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Daily Details */}
            <Card>
              <CardContent className="p-4 sm:p-5 md:p-6">
                <h3 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 flex items-center">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-primary" />
                  {isEnglish ? "Today's Dhikr Details" : "Detail Dzikir Hari Ini"}
                </h3>

                {history.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <XCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground text-sm sm:text-base">
                      {isEnglish
                        ? "No dhikr recorded yet today"
                        : "Belum ada dzikir yang tercatat hari ini"}
                    </p>
                    <Button
                      className="mt-3 sm:mt-4 text-sm"
                      onClick={() => navigate("/doa/tasbih")}
                    >
                      {isEnglish ? "Start Dhikr" : "Mulai Dzikir"}
                    </Button>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] sm:h-[400px]">
                    <div className="space-y-2 sm:space-y-3">
                      {history.map((log, index) => {
                        const label = getDhikirLabel(log.dzikir_id, log.log_type);
                        const progress = label.target > 0
                          ? Math.min((log.count / label.target) * 100, 100)
                          : 0;
                        const isComplete = log.count >= label.target && label.target > 0;

                        return (
                          <Card
                            key={index}
                            className={`${isComplete ? "border-green-500 border" : ""}`}
                          >
                            <CardContent className="p-3 sm:p-4">
                              <div className="flex items-start gap-2 sm:gap-3">
                                <div className="text-xl sm:text-2xl flex-shrink-0">{label.icon}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                                    <div className="min-w-0 flex-1 mr-2">
                                      <p className="font-semibold text-xs sm:text-sm truncate">
                                        {isEnglish ? label.en : label.id}
                                      </p>
                                      <p className="font-arabic text-[10px] sm:text-xs text-right" dir="rtl">
                                        {label.arabic}
                                      </p>
                                    </div>
                                    <Badge variant={isComplete ? "default" : "secondary"} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 flex-shrink-0">
                                      {label.isTimer ? (
                                        formatDuration(log.duration_seconds)
                                      ) : isComplete ? (
                                        <>
                                          <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                                          <span className="hidden xs:inline">{isEnglish ? "Complete" : "Selesai"}</span>
                                        </>
                                      ) : (
                                        `${log.count}${label.target > 0 ? '/' + label.target : ''}`
                                      )}
                                    </Badge>
                                  </div>

                                  {label.target > 0 && !label.isTimer && (
                                    <div className="space-y-0.5 sm:space-y-1">
                                      <div className="bg-muted rounded-full h-1.5 sm:h-2 overflow-hidden">
                                        <div
                                          className={`h-full transition-all ${
                                            isComplete ? "bg-green-500" : "bg-primary"
                                          }`}
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                                        {isEnglish ? "Progress" : "Progres"}: {progress.toFixed(0)}%
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Weekly Overview */}
            <Card>
              <CardContent className="p-4 sm:p-5 md:p-6">
                <h3 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 flex items-center">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-primary" />
                  {isEnglish ? "Last 7 Days" : "7 Hari Terakhir"}
                </h3>

                <div className="space-y-1.5 sm:space-y-2">
                  {weeklyStats.map((stat, index) => {
                    const maxTotal = Math.max(...weeklyStats.map(s => s.total), 100);
                    const barWidth = stat.total > 0 ? (stat.total / maxTotal) * 100 : 0;
                    const dateObj = new Date(stat.date);
                    const dayName = isEnglish
                      ? dateObj.toLocaleDateString('en-US', { weekday: 'short' })
                      : dateObj.toLocaleDateString('id-ID', { weekday: 'short' });

                    return (
                      <div key={index} className="flex items-center gap-2 sm:gap-3">
                        <div className="w-12 sm:w-16 text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">
                          {dayName}
                        </div>
                        <div className="flex-1 bg-muted rounded-full h-5 sm:h-6 overflow-hidden relative">
                          <div
                            className="bg-primary h-full transition-all duration-300"
                            style={{ width: `${barWidth}%` }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center text-[10px] sm:text-xs font-semibold">
                            {stat.total > 0 ? stat.total : '-'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Weekly Total */}
                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {isEnglish ? "Weekly Total" : "Total Mingguan"}
                    </span>
                    <Badge variant="secondary" className="text-sm sm:text-base px-2 sm:px-3 py-0.5 sm:py-1">
                      {weeklyStats.reduce((sum, stat) => sum + stat.total, 0)} {isEnglish ? "dhikr" : "dzikir"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default DhikrHistory;
